import React, { useCallback, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import YearSelector from './components/YearSelector';
import UserModuleMatrix, {
  UserModuleMatrixHandle,
  UserPrefRow,
} from './components/UserModuleMatrix';
import OperatorFilter, { OperatorOption } from './components/OperatorFilter';
import CopyToYearsDialog from './components/CopyToYearsDialog';
import {
  CopyButton,
  DangerButton,
  GlobalActionsBar,
  SaveButton,
  SectionLabel,
  ToggleAllButton,
} from './UserPreferences.styled';
import NotificationSnackbar from '../../common/components/Snackbar/Snackbar';
import { useSaveUserPreferences, useSettingsYears, useUserPreferences } from '@/hooks';
import { SaveUserPreferenceItem } from '@/types/userPreferences.ts';
import axios from '../../common/api/request';
import { DesignatedOperatorRelationsResponse } from '@/types/designatedOperatorRelations';

const UserPreferences: React.FC = () => {
  const { t } = useTranslation();
  const matrixRef = useRef<UserModuleMatrixHandle>(null);

  const [selectedYear, setSelectedYear] = useState<string>('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [matrixRefreshKey, setMatrixRefreshKey] = useState(0);

  // Bulk-save mutation (invalidates user-preferences cache on success)
  const saveMutation = useSaveUserPreferences({
    onSuccess: () => {
      setHasUnsavedChanges(false);
      setMatrixRefreshKey((k) => k + 1);
      setNotification({
        open: true,
        message: t('userPreferences.save.success', 'Preferences saved successfully'),
        severity: 'success',
      });
    },
    onError: () => {
      setNotification({
        open: true,
        message: t('userPreferences.save.error', 'Failed to save preferences. Please try again.'),
        severity: 'error',
      });
    },
  });

  // Operator filter state
  const [availableOperators, setAvailableOperators] = useState<OperatorOption[]>([]);
  const [selectedOperators, setSelectedOperators] = useState<OperatorOption[]>([]);

  // Years that have saved custom preferences (provided by YearSelector)
  const [customizedYears, setCustomizedYears] = useState<Set<string>>(new Set());

  // Derived: whether the currently selected year has saved preferences
  const hasSavedPreferences = customizedYears.has(selectedYear);

  // Unsaved-changes guard dialog
  const [pendingYear, setPendingYear] = useState<string | null>(null);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);

  // Reset-to-defaults confirmation dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Reset-all confirmation dialog
  const [resetAllDialogOpen, setResetAllDialogOpen] = useState(false);

  // Import from Global Settings dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  // Copy to years dialog
  const [copyToYearsDialogOpen, setCopyToYearsDialogOpen] = useState(false);

  // All saved preferences (for building the "reset all" payload)
  const { preferences: allPreferences } = useUserPreferences();

  // Whether the user has any saved customizations at all
  const hasAnyCustomizations = customizedYears.size > 0;

  // Separate mutation for "Reset All" so its callbacks don't clash with normal save
  const resetAllMutation = useSaveUserPreferences({
    onSuccess: () => {
      // Force the matrix to re-fetch saved preferences (now empty)
      setMatrixRefreshKey((k) => k + 1);
      setHasUnsavedChanges(false);
      setNotification({
        open: true,
        message: t(
          'userPreferences.resetAll.success',
          'All preferences have been reset to defaults'
        ),
        severity: 'success',
      });
    },
    onError: () => {
      setNotification({
        open: true,
        message: t(
          'userPreferences.resetAll.error',
          'Failed to reset preferences. Please try again.'
        ),
        severity: 'error',
      });
    },
  });
  const isResettingAll = resetAllMutation.isPending;

  // Notification snackbar
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const handleMatrixChange = useCallback((_rows: UserPrefRow[]) => {
    setHasUnsavedChanges(true);
  }, []);

  const handleAvailableOperators = useCallback((operators: string[]) => {
    setAvailableOperators(operators.map((op) => ({ targetDO: op })));
  }, []);

  const handleCustomizedYears = useCallback((years: Set<string>) => {
    setCustomizedYears(years);
  }, []);

  // Derive the filter list for the matrix (empty = show all)
  const filterOperators = selectedOperators.map((op) => op.targetDO);

  // ── Year change with unsaved-changes guard ──────────────────────────────

  const applyYearChange = (year: string) => {
    setSelectedYear(year);
    setHasUnsavedChanges(false);
    setSelectedOperators([]);
  };

  const handleYearChange = (year: string) => {
    applyYearChange(year);
  };

  const handleUnsavedYearChange = (year: string) => {
    setPendingYear(year);
    setUnsavedDialogOpen(true);
  };

  const handleConfirmYearChange = () => {
    if (pendingYear) {
      applyYearChange(pendingYear);
    }
    setUnsavedDialogOpen(false);
    setPendingYear(null);
  };

  const handleCancelYearChange = () => {
    setUnsavedDialogOpen(false);
    setPendingYear(null);
  };

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = () => {
    const changedRows = matrixRef.current?.getChangedRows();
    if (!changedRows || changedRows.length === 0 || !selectedYear) return;

    const payload: SaveUserPreferenceItem[] = changedRows.map((row) => ({
      accountingYear: parseInt(selectedYear),
      doName: row.targetDO,
      modules: Object.entries(row.modules).map(([name, enabled]) => ({
        name,
        enabled,
      })),
    }));

    saveMutation.mutate(payload);
  };

  // ── Reset to defaults ───────────────────────────────────────────────────

  const handleResetClick = () => {
    setResetDialogOpen(true);
  };

  const handleConfirmReset = () => {
    matrixRef.current?.resetRows();
    setHasUnsavedChanges(true);
    setResetDialogOpen(false);
  };

  const handleCancelReset = () => {
    setResetDialogOpen(false);
  };

  // ── Reset ALL preferences (all years) ────────────────────────────────────

  const handleResetAllClick = () => {
    setResetAllDialogOpen(true);
  };

  const handleConfirmResetAll = () => {
    setResetAllDialogOpen(false);

    // Build a payload with every existing preference row set to all-disabled
    const payload: SaveUserPreferenceItem[] = allPreferences.map((pref) => ({
      accountingYear: pref.accountingYear,
      doName: pref.doName,
      modules: pref.modules.map((m) => ({ name: m.name, enabled: false })),
    }));

    if (payload.length === 0) return;
    resetAllMutation.mutate(payload);
  };

  const handleCancelResetAll = () => {
    setResetAllDialogOpen(false);
  };

  // ── Import from Global Settings ───────────────────────────────────────────

  const importMutation = useSaveUserPreferences({
    onSuccess: () => {
      // Force the matrix to re-fetch saved preferences (now reflecting imported data)
      setMatrixRefreshKey((k) => k + 1);
      setHasUnsavedChanges(false);
      setNotification({
        open: true,
        message: t(
          'userPreferences.importSettings.success',
          'Default settings have been imported as your preferences'
        ),
        severity: 'success',
      });
    },
    onError: () => {
      setNotification({
        open: true,
        message: t(
          'userPreferences.importSettings.error',
          'Failed to import settings. Please try again.'
        ),
        severity: 'error',
      });
    },
  });
  const isImporting = importMutation.isPending;

  // ── Copy to Years ─────────────────────────────────────────────────────────

  // All available years from Settings superset (for the copy dialog)
  const { years: allSettingsYears } = useSettingsYears();

  const copyToYearsMutation = useSaveUserPreferences({
    onSuccess: () => {
      setMatrixRefreshKey((k) => k + 1);
      setHasUnsavedChanges(false);
      setNotification({
        open: true,
        message: t('userPreferences.copyToYears.success', 'Preferences copied successfully'),
        severity: 'success',
      });
    },
    onError: () => {
      setNotification({
        open: true,
        message: t(
          'userPreferences.copyToYears.error',
          'Failed to copy preferences. Please try again.'
        ),
        severity: 'error',
      });
    },
  });
  const isCopying = copyToYearsMutation.isPending;

  const isSaving = saveMutation.isPending || isResettingAll || isImporting || isCopying;

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const handleCancelImport = () => {
    setImportDialogOpen(false);
  };

  const handleConfirmImport = async () => {
    setImportDialogOpen(false);

    try {
      // Fetch all designated operator relations (the global Settings superset)
      const resp = await axios.get<DesignatedOperatorRelationsResponse>(
        '/designated-operator-relations'
      );
      const allRelations = resp.data;

      if (!allRelations?.byYear) {
        setNotification({
          open: true,
          message: t(
            'userPreferences.importSettings.noData',
            'No global settings found to import.'
          ),
          severity: 'warning',
        });
        return;
      }

      // Convert every Settings relation into a SaveUserPreferenceItem,
      // preserving the enabled state from Settings for each module.
      const payload: SaveUserPreferenceItem[] = [];

      Object.values(allRelations.byYear).forEach((relations) => {
        relations.forEach((rel) => {
          payload.push({
            accountingYear: rel.year,
            doName: rel.targetDO,
            modules: rel.modules.map((m) => ({
              name: m.name.toLowerCase(),
              enabled: m.enabled,
            })),
          });
        });
      });

      if (payload.length === 0) {
        setNotification({
          open: true,
          message: t(
            'userPreferences.importSettings.noData',
            'No global settings found to import.'
          ),
          severity: 'warning',
        });
        return;
      }

      importMutation.mutate(payload);
    } catch {
      setNotification({
        open: true,
        message: t(
          'userPreferences.importSettings.error',
          'Failed to import settings. Please try again.'
        ),
        severity: 'error',
      });
    }
  };

  // ── Copy to Years handlers ────────────────────────────────────────────────

  const handleCopyToYearsClick = () => {
    setCopyToYearsDialogOpen(true);
  };

  const handleCancelCopyToYears = () => {
    setCopyToYearsDialogOpen(false);
  };

  const handleConfirmCopyToYears = (targetYears: string[]) => {
    setCopyToYearsDialogOpen(false);

    // Get ALL current rows from the matrix (the state for the selected year)
    const currentRows = matrixRef.current?.getRows();
    if (!currentRows || currentRows.length === 0 || !selectedYear) {
      setNotification({
        open: true,
        message: t(
          'userPreferences.copyToYears.noData',
          'No preferences to copy. Please configure preferences first.'
        ),
        severity: 'warning',
      });
      return;
    }

    // Build the payload: for each target year, replicate every row's modules
    const payload: SaveUserPreferenceItem[] = [];

    // Include the current year as well (saves any unsaved changes)
    const allYears = [selectedYear, ...targetYears];
    const uniqueYears = [...new Set(allYears)];

    uniqueYears.forEach((year) => {
      currentRows.forEach((row) => {
        payload.push({
          accountingYear: parseInt(year),
          doName: row.targetDO,
          modules: Object.entries(row.modules).map(([name, enabled]) => ({
            name,
            enabled,
          })),
        });
      });
    });

    copyToYearsMutation.mutate(payload);
  };

  // ── Check / Uncheck all ──────────────────────────────────────────────────

  const handleToggleAll = () => {
    const allChecked = matrixRef.current?.areAllChecked();
    if (allChecked) {
      matrixRef.current?.uncheckAll();
    } else {
      matrixRef.current?.checkAll();
    }
    setHasUnsavedChanges(true);
  };

  // ── Notification close ──────────────────────────────────────────────────

  const handleNotificationClose = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Paper elevation={0} sx={{ p: 2 }} data-testid='user-preferences-container'>
      <Box sx={{ width: '100%' }}>
        <Box sx={{ mt: 3 }}>
          {/* ── Status banner ──────────────────────────────────────── */}
          <Alert
            severity={hasAnyCustomizations ? 'success' : 'info'}
            sx={{ mb: 2, maxWidth: '770px' }}
            data-testid='user-preferences-status-banner'
          >
            {hasAnyCustomizations
              ? t(
                  'userPreferences.banner.hasPreferences',
                  "Custom preferences active for: {{years}}. Data outside these years won't be shown.",
                  { years: Array.from(customizedYears).sort().join(', ') }
                )
              : t(
                  'userPreferences.banner.noPreferences',
                  'There are no preferences for any year. Global default settings are being used.'
                )}
          </Alert>
          {/* ── Global actions (apply to all years) ──────────────── */}
          <GlobalActionsBar data-testid='user-preferences-global-actions'>
            <SectionLabel>{t('userPreferences.sections.globalActions', 'All years')}</SectionLabel>

            {/*<ImportButton*/}
            {/*  variant='outlined'*/}
            {/*  onClick={handleImportClick}*/}
            {/*  disabled={isSaving}*/}
            {/*  data-testid='user-preferences-import-settings-button'*/}
            {/*>*/}
            {/*  {isImporting*/}
            {/*    ? t('userPreferences.actions.importing', 'Importing...')*/}
            {/*    : t('userPreferences.actions.importSettings', 'Import from settings')}*/}
            {/*</ImportButton>*/}
            <CopyButton
              variant='outlined'
              onClick={handleCopyToYearsClick}
              disabled={isSaving || !selectedYear}
              data-testid='user-preferences-copy-to-years-button'
            >
              {isCopying
                ? t('userPreferences.actions.copying', 'Copying...')
                : t('userPreferences.actions.copyToYears', 'Copy to years...')}
            </CopyButton>
            <DangerButton
              variant='outlined'
              onClick={handleResetAllClick}
              disabled={isSaving || !hasAnyCustomizations}
              data-testid='user-preferences-reset-all-button'
            >
              {isResettingAll
                ? t('userPreferences.actions.resettingAll', 'Resetting...')
                : t('userPreferences.actions.resetAll', 'Reset all')}
            </DangerButton>
          </GlobalActionsBar>

          {/* ── Year selector ──────────────────────────────────────── */}
          <Box sx={{ mb: 2, maxWidth: '770px' }}>
            <YearSelector
              selectedYear={selectedYear}
              onYearChange={handleYearChange}
              hasUnsavedChanges={hasUnsavedChanges}
              onUnsavedYearChange={handleUnsavedYearChange}
              onCustomizedYears={handleCustomizedYears}
            />
          </Box>

          {/* ── Per-year actions ────────────────────────────────────── */}
          {selectedYear && (
            <Box
              sx={{
                mb: 3,
                maxWidth: '770px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <SaveButton
                variant='contained'
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                data-testid='user-preferences-save-button'
              >
                {isSaving
                  ? t('userPreferences.actions.saving', 'Saving...')
                  : t('userPreferences.actions.save', {
                      defaultValue: 'Save {{year}} preferences',
                      year: selectedYear,
                    })}
              </SaveButton>
              <ToggleAllButton
                variant='outlined'
                onClick={handleToggleAll}
                disabled={isSaving}
                data-testid='user-preferences-toggle-all-button'
              >
                {t('userPreferences.actions.uncheckAll', 'Toggle all')}
              </ToggleAllButton>
            </Box>
          )}

          {selectedYear && (
            <OperatorFilter
              availableOperators={availableOperators}
              selectedOperators={selectedOperators}
              onChange={setSelectedOperators}
            />
          )}

          <UserModuleMatrix
            ref={matrixRef}
            selectedYear={selectedYear}
            onChange={handleMatrixChange}
            filterOperators={filterOperators}
            onAvailableOperators={handleAvailableOperators}
            refreshKey={matrixRefreshKey}
          />
        </Box>
      </Box>

      {/* Unsaved-changes guard dialog */}
      <Dialog
        open={unsavedDialogOpen}
        onClose={handleCancelYearChange}
        data-testid='user-preferences-unsaved-dialog'
      >
        <DialogTitle>{t('userPreferences.unsavedDialog.title', 'Unsaved Changes')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'userPreferences.unsavedDialog.message',
              'You have unsaved changes. Changing the year will discard them. Do you want to continue?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelYearChange}>
            {t('userPreferences.unsavedDialog.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmYearChange} color='primary' variant='contained'>
            {t('userPreferences.unsavedDialog.confirm', 'Discard & Continue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset-to-defaults confirmation dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleCancelReset}
        data-testid='user-preferences-reset-dialog'
      >
        <DialogTitle>{t('userPreferences.resetDialog.title', 'Reset to Defaults')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'userPreferences.resetDialog.message',
              'This will uncheck all operator/module selections for the selected year. You will still need to save to apply the changes. Continue?'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelReset}>
            {t('userPreferences.resetDialog.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmReset} color='error' variant='contained'>
            {t('userPreferences.resetDialog.confirm', 'Reset')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset ALL preferences confirmation dialog */}
      <Dialog
        open={resetAllDialogOpen}
        onClose={handleCancelResetAll}
        data-testid='user-preferences-reset-all-dialog'
      >
        <DialogTitle>
          {t('userPreferences.resetAllDialog.title', 'Reset All Preferences')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'userPreferences.resetAllDialog.message',
              'This will permanently remove all your custom preferences for every year. All module pages will revert to showing the full default scope. This action cannot be undone.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelResetAll}>
            {t('userPreferences.resetAllDialog.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmResetAll} color='error' variant='contained'>
            {t('userPreferences.resetAllDialog.confirm', 'Reset all preferences')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import from Global Settings confirmation dialog */}
      <Dialog
        open={importDialogOpen}
        onClose={handleCancelImport}
        data-testid='user-preferences-import-settings-dialog'
      >
        <DialogTitle>
          {t('userPreferences.importSettingsDialog.title', 'Import from Global Settings')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              'userPreferences.importSettingsDialog.message',
              'This will copy the current global Settings configuration into your personal preferences for all years. Any existing preferences will be overwritten. This action saves immediately and cannot be undone.'
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImport}>
            {t('userPreferences.importSettingsDialog.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleConfirmImport} color='primary' variant='contained'>
            {t('userPreferences.importSettingsDialog.confirm', 'Import')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy to Years dialog */}
      <CopyToYearsDialog
        open={copyToYearsDialogOpen}
        availableYears={allSettingsYears}
        currentYear={selectedYear}
        isSaving={isCopying}
        onConfirm={handleConfirmCopyToYears}
        onCancel={handleCancelCopyToYears}
      />

      {/* Notification snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleNotificationClose}
        data-testid='user-preferences-notification'
      />
    </Paper>
  );
};

export default UserPreferences;
